<template>
  <div class="h-100 d-flex flex-column align-center" :class="{ 'justify-space-around': !shouldFloat, 'float-container': shouldFloat }" ref="outerDiv">
    <div
      v-if="system"
      :id="`system-${system.index}`"
      :class="{'elevation-1 service-border pa-2': hasRelatedEntities, 'sticky-float': shouldFloat}"
      :style="{ ...stickyStyle, ...(hasRelatedEntities ? { '--service-border-color': cardTypeColor(entityCardType) } : {}) }"
      ref="systemDiv"
    >
      <div>
        <div
          v-if="system.groupedBy"
          class="card-item hover-container fit-content"
          :class="{ 'card-item-wide': useTwoColumnFields }"
          :style="{ 'border-color': cardTypeColor(entityCardType) }"
        >
          <div :id="system.id">
            <select-entity-combobox
              :id="currentSystemEntitiesData.rootEntity?.id"
              :events="system.events"
              :boundedContextId="boundedContext.id"
              :cardIds="system.entityCards.map((card) => card.id)"
              :style="{ '--borderColor': cardTypeColor(entityCardType), '--borderTopWidth': '1px' }"
            />
          </div>
          <div
            v-if="showEntities && currentSystemEntitiesData.rootEntity?.fields?.length > 0"
            class="py-2"
            :class="{ 'root-fields-two-column': useTwoColumnFields }"
          >
            <div
              v-for="(field, index4) in currentSystemEntitiesData.rootEntity.fields"
              :key="index4"
              class="field-name d-flex align-center position-relative"
              :class="{ 'unused-field': !isFieldUsed(field) }"
            >
              <quick-rename-input
                :id="`root-entity-${currentSystemEntitiesData.rootEntity.id}-field-${index4}`"
                :value="field.name"
                @onChange="blurEntityField($event, currentSystemEntitiesData.rootEntity, index4)"
                :density="`compact`"
                :max-length="30"
                class="flex-1-0"
                :class="{
                  'required-field': field.tags?.includes('REQUIRED'),
                  'unused-field': !isFieldUsed(field)
                }"
                :disabled="store.state.isWorkflowDisabled"
              />
              <span v-if="dataTypeLabel(field)" class="data-type-label">{{ dataTypeLabel(field) }}</span>
              <v-chip v-if="field.relatedEntityId && getRelatedEntity(field)" density="compact" size="small" class="px-2 ml-1">
                {{ field.cardinality === 'one-to-many'
                  ? `[ ${getRelatedEntity(field)?.name} ]`
                  : `${getRelatedEntity(field)?.name}` }}
              </v-chip>
            </div>
          </div>
          <div class="d-flex justify-space-between align-center ga-2">
            <div class="show-on-hover">
              <edit-entity
                v-if="currentSystemEntitiesData.rootEntity"
                :entity="currentSystemEntitiesData.rootEntity"
                :refreshKey="refreshKey"
              />
            </div>
            <div class="pt-1">
              <workflow-aggregate-select-bounded-context
                :cards="currentSystemEntitiesData.rootEntity ? null : system.entityCards"
                :entity="currentSystemEntitiesData.rootEntity || null"
                :boundedContext="boundedContext"
              />
            </div>
          </div>
        </div>
        <v-btn
          v-else-if="entityCardType"
          class="add-card-btn"
          variant="elevated"
          block
          data-cy="add-aggregate-root-btn"
          :style="{ '--border-color': cardTypeColor(entityCardType) }"
          @click="createCard(system.events[0].id, entityCardType.id, `<p>Aggregate Root for ${system.events[0].description}</p>`)"
          :disabled="store.state.isWorkflowDisabled"
        >
          <v-icon size="large">mdi-plus</v-icon>
        </v-btn>
        <aggregate-type-selector
          v-else
          :cardTypes="cardTypes"
          :type="DomainModelRole.AGGREGATE_ROOT"
        />

        <!-- System: entities under aggregate root -->
        <div
          v-if="showEntities && system.groupedBy && findEntitiesInBoundedContext(system.groupedBy.boundedContext).length > 0"
          class="d-flex justify-space-between ga-4"
        >
          <template v-for="columnName in ['left', 'right']" :key="columnName">
            <div class="d-flex flex-column align-center w-100" style="min-width: 150px; font-size: 13px;">
              <template v-for="(entity, index) in groupedEntities(columnName, system.groupedBy.boundedContext)" :key="index">
                <v-card
                  :id="`${system.id}-${columnName}-end-element-${entity.id}`"
                  class="d-flex flex-column align-start mt-5 bg-surface hover-container w-100"
                  :ref="`left-entities-${index}`"
                >
                  <v-card-text
                    class="card-item-body rounded-lg pa-1 hover-container"
                    :style="{ 'border-color': cardTypeColor(entityCardType) }"
                  >
                    <div :id="entity.id" class="d-flex justify-space-between align-center ga-2 mb-1">
                      {{ entity.name }}
                      <div class="show-on-hover pe-2">
                        <edit-entity :entity="entity" :refreshKey="refreshKey" />
                      </div>
                    </div>
                    <template v-if="showEntities">
                      <v-divider thickness="2" style="width: 100%;"></v-divider>
                      <template v-for="(field, index2) in entity.fields" :key="index2">
                        <div
                          v-if="field && field.name"
                          class="field-name d-flex align-center"
                          :class="{ 'unused-field': !isEntityFieldUsed(entity, field.name) }"
                        >
                          <quick-rename-input
                            :id="`entity-${entity.id}-field-${index2}`"
                            :value="field.name"
                            @onChange="(e) => blurEntityField(e, entity, index2)"
                            :density="`compact`"
                            :max-length="30"
                            class="flex-1-0"
                            :class="{
                              'required-field': field.tags?.includes('REQUIRED'),
                              'unused-field': !isEntityFieldUsed(entity, field.name)
                            }"
                            :disabled="store.state.isWorkflowDisabled"
                          />
                          <span v-if="dataTypeLabel(field)" class="data-type-label">{{ dataTypeLabel(field) }}</span>
                          <v-chip v-if="field.relatedEntityId && getRelatedEntity(field)" density="compact" size="small" class="px-2 ml-1">
                            {{ field.cardinality === 'one-to-many'
                              ? `[ ${getRelatedEntity(field)?.name} ]`
                              : `${getRelatedEntity(field)?.name}` }}
                          </v-chip>
                        </div>
                      </template>
                    </template>
                    <div class="pt-1">
                      <workflow-aggregate-select-bounded-context
                        :entity="entity"
                        :boundedContext="boundedContext"
                      />
                    </div>
                  </v-card-text>
                </v-card>
                <connecting-path
                  :endElementId="`${system.id}-${columnName}-end-element-${entity.id}`"
                  :startElementId="system.id"
                  :refreshKey="refreshKey"
                  :multiline="entity.isOneToMany"
                  start="bottom"
                  end="top"
                />
                <template v-for="childEntity in getChildEntities(entity)" :key="childEntity.id">
                  <domain-model-nested-entity
                    :childEntity="childEntity"
                    :parentEntity="entity"
                    :cardElementId="`${system.id}-${columnName}-child-${entity.id}-${childEntity.id}`"
                    :boundedContext="boundedContext"
                    :refreshKey="refreshKey"
                    :showEntities="showEntities"
                    :borderColor="childBorderColor(childEntity)"
                    @field-blur="blurEntityField"
                  />
                  <connecting-path
                    :endElementId="`${system.id}-${columnName}-child-${entity.id}-${childEntity.id}`"
                    :startElementId="`${system.id}-${columnName}-end-element-${entity.id}`"
                    :refreshKey="refreshKey"
                    :multiline="childEntity.isOneToMany"
                    start="bottom"
                    end="left"
                    :startXOffset="10"
                  />
                </template>
              </template>
            </div>
          </template>
        </div>
      </div>

      <!-- System: entities under integration with other contexts -->
      <template v-if="showEntities && system.groupedBy && findEntitiesNotInBoundedContext(system.groupedBy.boundedContext).length > 0">
        <div class="integration-divider text-white my-2 mb-0">Integration with other bounded contexts</div>
        <div class="d-flex justify-space-between ga-4">
          <template v-for="columnName in ['left', 'right']" :key="columnName">
            <div class="d-flex flex-column align-center flex-shrink-0" style="min-width: 150px; font-size: 13px;">
              <template
                v-for="(entity, index) in groupedEntities(columnName, system.groupedBy.boundedContext, true)"
                :key="index"
              >
                <v-card
                  :id="`${system.id}-integration-${columnName}-end-element-${entity.id}`"
                  class="d-flex flex-column align-start mt-5 bg-surface hover-container"
                  min-width="150"
                  :ref="`left-entities-${index}`"
                >
                  <v-card-text
                    class="card-item-body rounded-lg pa-1 hover-container"
                    :style="{ 'border-color': 'var(--dark-2)' }"
                  >
                    <div :id="entity.id" class="d-flex justify-space-between align-center ga-2 mb-1">
                      {{ entity.name }}
                      <div class="show-on-hover pe-2">
                        <edit-entity :entity="entity" :refreshKey="refreshKey" />
                      </div>
                    </div>
                    <template v-if="showEntities">
                      <v-divider thickness="2" style="width: 100%;"></v-divider>
                      <template v-for="(field, index2) in entity.fields" :key="index2">
                        <div
                          v-if="field && field.name"
                          class="field-name d-flex align-center"
                          :class="{ 'unused-field': !isEntityFieldUsed(entity, field.name) }"
                        >
                          <quick-rename-input
                            :id="`entity-${entity.id}-field-${index2}`"
                            :value="field.name"
                            @onChange="(e) => blurEntityField(e, entity, index2)"
                            :density="`compact`"
                            :max-length="30"
                            class="flex-1-0"
                            :class="{
                              'required-field': field.tags?.includes('REQUIRED'),
                              'unused-field': !isEntityFieldUsed(entity, field.name)
                            }"
                            :disabled="store.state.isWorkflowDisabled"
                          />
                          <span v-if="dataTypeLabel(field)" class="data-type-label">{{ dataTypeLabel(field) }}</span>
                          <v-chip v-if="field.relatedEntityId && getRelatedEntity(field)" density="compact" size="small" class="px-2 ml-1">
                            {{ field.cardinality === 'one-to-many'
                              ? `[ ${getRelatedEntity(field)?.name} ]`
                              : `${getRelatedEntity(field)?.name}` }}
                          </v-chip>
                        </div>
                      </template>
                    </template>
                    <div class="pt-1">
                      <workflow-aggregate-select-bounded-context
                        :entity="entity"
                        :boundedContext="boundedContext"
                      />
                    </div>
                  </v-card-text>
                </v-card>
                <connecting-path
                  :endElementId="`${system.id}-integration-${columnName}-end-element-${entity.id}`"
                  :startElementId="system.id"
                  :refreshKey="refreshKey"
                  :multiline="entity.isOneToMany"
                  start="bottom"
                  end="top"
                />
                <template v-for="childEntity in getChildEntities(entity)" :key="childEntity.id">
                  <domain-model-nested-entity
                    :childEntity="childEntity"
                    :parentEntity="entity"
                    :cardElementId="`${system.id}-integration-${columnName}-child-${entity.id}-${childEntity.id}`"
                    :boundedContext="boundedContext"
                    :refreshKey="refreshKey"
                    :showEntities="showEntities"
                    :borderColor="childBorderColor(childEntity)"
                    @field-blur="blurEntityField"
                  />
                  <connecting-path
                    :endElementId="`${system.id}-integration-${columnName}-child-${entity.id}-${childEntity.id}`"
                    :startElementId="`${system.id}-integration-${columnName}-end-element-${entity.id}`"
                    :refreshKey="refreshKey"
                    :multiline="childEntity.isOneToMany"
                    start="bottom"
                    end="left"
                    :startXOffset="10"
                  />
                </template>
              </template>
            </div>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import EditEntity from '@/components/EditEntity'
import ConnectingPath from '@/components/ConnectingPath'
import QuickRenameInput from '@/components/QuickRenameInput'
import SelectEntityCombobox from '@/components/SelectEntityCombobox'
import AggregateTypeSelector from '@/components/AggregateTypeSelector'
import DomainModelNestedEntity from '@/components/DomainModelNestedEntity'
import WorkflowAggregateSelectBoundedContext from '@/components/WorkflowAggregateSelectBoundedContext'
import { fieldNameImpliesEntity, fieldNameIsEntityIdReference, isSingularPluralMatch } from '@/utils/fieldNameMatch'
import useDomainModel, { shouldDeferSchemaUpdate, setPendingSchemaChange, applyPendingChanges } from '@/composables/useDomainModel'
import workflowApi from '@/api/workflowApi'
import safeStructuredClone from '@/utils/clone'
import { cardHelpers } from '@/mixins/cardHelpers'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import { DomainModelRole } from '@/constants/cardTypes'
import { DataTypeShortLabels } from '@/constants/schema'

const { system, refreshKey, boundedContext, showEntities, currentSystemEntitiesData } = defineProps({
  system: Object,
  refreshKey: Number,
  boundedContext: Object,
  showEntities: Boolean,
  currentSystemEntitiesData: Object
})
const emit = defineEmits(['refreshArrows'])

const store = useStore()
const route = useRoute()
const { validateFieldName, cardTypeColor, getRelatedEntity, createCard } = useDomainModel()

const outerDiv = ref(null)
const systemDiv = ref(null)
const resizeObserver = ref(null)
const shouldFloat = ref(false)
const stickyTop = ref(0)
const isTallerThanViewport = ref(false)

const STICKY_MARGIN = 50
let lastScrollY = window.scrollY
let currentDynamicTop = STICKY_MARGIN
let cachedMinTop = 0
let cachedMaxTop = STICKY_MARGIN

const workflow = computed(() => store.state.workflow.workflow)
const cardTypes = computed(() => store.state.cardTypes.cardTypes)
const entityCardType = computed(() => diagramHelpers.methods.getAggregateCardType(cardTypes.value))

const hasRelatedEntities = computed(() => {
  return system.groupedBy && showEntities && currentSystemEntitiesData.entities.length > 0
})

const useTwoColumnFields = computed(() => {
  return (currentSystemEntitiesData.rootEntity?.fields?.length || 0) >= 10
})

const isSingleEventSelected = computed(() => {
  return !!route.params.eventId
})

const stickyStyle = computed(() => {
  const base = { zIndex: 1 }
  if (shouldFloat.value) {
    base.top = stickyTop.value + 'px'
  }

  return base
})

// Extract field names from commands based on selection state
const usedFieldNames = computed(() => {
  if (!system?.events?.length) {
    return new Set()
  }

  const fieldNames = new Set()
  const commandSchemas = getCommandSchemas()

  commandSchemas.forEach((commandSchema) => {
    if (commandSchema.fields) {
      commandSchema.fields.forEach((field) => {
        if (field.name) {
          fieldNames.add(field.name)
        }
      })
    }
  })

  return fieldNames
})

// Memoized map of entity IDs to Sets of used nested field names
const entityUsedFieldNames = computed(() => {
  const entityFieldMap = new Map()
  const commandSchemas = getCommandSchemas()
  const allEntities = [
    currentSystemEntitiesData.rootEntity,
    ...(currentSystemEntitiesData.entities || [])
  ].filter(Boolean)

  commandSchemas.forEach((commandSchema) => {
    if (commandSchema.fields) {
      commandSchema.fields.forEach((commandField) => {
        // Check if this command field references an entity
        if (commandField.relatedEntityId && commandField.fields) {
          const entityId = commandField.relatedEntityId
          if (!entityFieldMap.has(entityId)) {
            entityFieldMap.set(entityId, new Set())
          }
          const usedFields = entityFieldMap.get(entityId)
          commandField.fields.forEach((nestedField) => {
            if (nestedField.name) {
              usedFields.add(nestedField.name)
            }
          })
        }

        if (commandField.name) {
          const impliedEntity = allEntities.find((e) => fieldNameIsEntityIdReference(commandField.name, e.name))
          if (impliedEntity) {
            if (!entityFieldMap.has(impliedEntity.id)) {
              entityFieldMap.set(impliedEntity.id, new Set())
            }
            entityFieldMap.get(impliedEntity.id).add('id')
          }
        }

        if (commandField.name && !commandField.relatedEntityId) {
          const rootEntity = currentSystemEntitiesData.rootEntity
          const rootFieldMatch = rootEntity?.fields?.some((f) =>
            f.name === commandField.name || isSingularPluralMatch(f.name, commandField.name)
          )

          if (!rootFieldMatch) {
            for (const entity of (currentSystemEntitiesData.entities || [])) {
              const matchingField = (entity.fields || []).find((f) =>
                f.name === commandField.name || isSingularPluralMatch(f.name, commandField.name)
              )
              if (matchingField) {
                if (!entityFieldMap.has(entity.id)) {
                  entityFieldMap.set(entity.id, new Set())
                }
                entityFieldMap.get(entity.id).add(matchingField.name)
                break
              }
            }
          }
        }
      })
    }
  })

  return entityFieldMap
})

const dataTypeLabel = (field) => {
  return DataTypeShortLabels[field.dataType] || ''
}

const childBorderColor = (childEntity) => {
  if (childEntity.boundedContext === system.groupedBy?.boundedContext) {
    return cardTypeColor(entityCardType.value)
  }
  return 'var(--dark-2)'
}

const getCommandSchemas = () => {
  const commandSchemas = []

  if (!system?.events?.length) {
    return commandSchemas
  }

  if (isSingleEventSelected.value) {
    const selectedEvent = system.events.find((event) => event.id === route.params.eventId)
    if (selectedEvent) {
      const commandCard = cardHelpers.methods.getCommandCard(selectedEvent)
      if (commandCard?.schemaId) {
        const commandSchema = workflow.value.schemas?.find((schema) => schema.id === commandCard.schemaId)
        if (commandSchema) {
          commandSchemas.push(commandSchema)
        }
      }
    }
  } else {
    system.events.forEach((event) => {
      const commandCard = cardHelpers.methods.getCommandCard(event)
      if (commandCard?.schemaId) {
        const commandSchema = workflow.value.schemas?.find((schema) => schema.id === commandCard.schemaId)
        if (commandSchema) {
          commandSchemas.push(commandSchema)
        }
      }
    })
  }

  return commandSchemas
}

const isFieldUsed = (field) => {
  const fieldName = typeof field === 'string' ? field : field.name

  if (usedFieldNames.value.has(fieldName)) {
    return true
  }

  for (const usedName of usedFieldNames.value) {
    if (isSingularPluralMatch(fieldName, usedName)) {
      return true
    }
  }

  if (typeof field === 'object' && field.relatedEntityId) {
    const relatedEntity = workflow.value.schemas?.find((s) => s.id === field.relatedEntityId)
    if (relatedEntity && fieldNameImpliesEntity(fieldName, relatedEntity.name)) {
      for (const usedName of usedFieldNames.value) {
        if (fieldNameIsEntityIdReference(usedName, relatedEntity.name)) {
          return true
        }
      }
    }
  }

  return false
}

const isEntityFieldUsed = (entity, fieldName) => {
  const usedFields = entityUsedFieldNames.value.get(entity.id)
  return usedFields ? usedFields.has(fieldName) : false
}

// Determine the effective Level 2 entities, inserting missing intermediate entities
// when an entity in currentSystemEntitiesData is actually a grandchild of the root
const getLevel2Entities = (entityList) => {
  const rootEntity = currentSystemEntitiesData.rootEntity
  if (!rootEntity || !workflow.value.schemas) {
    return entityList
  }

  const rootFieldEntityIds = new Set(
    (rootEntity.fields || [])
      .filter((f) => f.relatedEntityId)
      .map((f) => f.relatedEntityId)
  )

  const level2 = []
  const level2Ids = new Set()

  for (const entity of entityList) {
    if (rootFieldEntityIds.has(entity.id)) {
      // Directly referenced by root - true Level 2
      if (!level2Ids.has(entity.id)) {
        level2.push(entity)
        level2Ids.add(entity.id)
      }
    } else {
      // Not directly referenced by root - find the intermediate entity
      // Look for an entity referenced by root whose fields reference this entity
      for (const rootField of (rootEntity.fields || [])) {
        if (!rootField.relatedEntityId) continue
        const intermediateEntity = workflow.value.schemas.find((s) => s.id === rootField.relatedEntityId)
        if (!intermediateEntity) continue
        const referencesChild = (intermediateEntity.fields || []).some((f) => f.relatedEntityId === entity.id)
        if (referencesChild && !level2Ids.has(intermediateEntity.id)) {
          level2.push(intermediateEntity)
          level2Ids.add(intermediateEntity.id)
        }
      }
    }
  }

  return level2
}

const groupedEntities = (columnName, boundedContext, notInBoundedContext = false) => {
  const res = []
  let index = 0
  const rootEntity = currentSystemEntitiesData.rootEntity

  if (workflow.value.schemas) {
    let entities
    if (notInBoundedContext) {
      entities = findEntitiesNotInBoundedContext(boundedContext)
    } else {
      entities = findEntitiesInBoundedContext(boundedContext)
    }

    const effectiveEntities = getLevel2Entities(entities)

    for (const entity of effectiveEntities) {
      const matchingFieldInRoot = rootEntity
        ? rootEntity.fields?.find((item) => item.relatedEntityId === entity.id)
        : null

      if (index % 2 === 0 && columnName === 'left') {
        res.push({
          ...entity,
          isOneToMany: matchingFieldInRoot?.cardinality === 'one-to-many',
          index
        })
      } else if (index % 2 !== 0 && columnName === 'right') {
        res.push({
          ...entity,
          isOneToMany: matchingFieldInRoot?.cardinality === 'one-to-many',
          index
        })
      }
      index++
    }
  }

  return res
}

const findEntitiesInBoundedContext = (boundedContext) => {
  return currentSystemEntitiesData.entities?.filter((model) => model.boundedContext === boundedContext)
}

const findEntitiesNotInBoundedContext = (boundedContext) => {
  return currentSystemEntitiesData.entities?.filter((model) => model.boundedContext !== boundedContext)
}

const getChildEntities = (parentEntity) => {
  if (!parentEntity?.id || !workflow.value.schemas) {
    return []
  }

  const schemas = workflow.value.schemas
  const commandSchemas = getCommandSchemas()
  const children = []
  const parentFields = schemas.find((s) => s.id === parentEntity.id)?.fields || []

  // Approach 1: Check command sub-fields nested under fields referencing the parent entity
  const commandSubFields = []
  for (const commandSchema of commandSchemas) {
    for (const field of (commandSchema.fields || [])) {
      if (field.relatedEntityId === parentEntity.id && field.fields?.length > 0) {
        commandSubFields.push(...field.fields)
      }
    }
  }

  const entitySchemas = schemas.filter((s) => s.type === 'Entity')
  for (const schema of entitySchemas) {
    if (schema.id === parentEntity.id) {
      continue
    }

    const isRelatedViaSubFields = commandSubFields.some(
      (subField) => subField.relatedEntityId === schema.id && subField.fields?.length > 0
    )

    const isIdReferencedViaSubFields = !isRelatedViaSubFields &&
      commandSubFields.length > 0 &&
      commandSubFields.some((subField) => fieldNameIsEntityIdReference(subField.name, schema.name)) &&
      parentFields.some((f) => f.relatedEntityId === schema.id)

    if (isRelatedViaSubFields || isIdReferencedViaSubFields) {
      if (children.every((c) => c.id !== schema.id)) {
        const matchingParentField = parentFields.find((f) => f.relatedEntityId === schema.id)
        children.push({
          ...schema,
          isOneToMany: matchingParentField?.cardinality === 'one-to-many'
        })
      }
    }
  }

  // Approach 2: Check if any entity in currentSystemEntitiesData is referenced by the parent
  // entity's fields but not directly by the root entity (i.e., it was promoted to Level 2
  // but actually belongs as Level 3 under this parent)
  const rootEntity = currentSystemEntitiesData.rootEntity
  const rootFieldEntityIds = new Set(
    (rootEntity?.fields || [])
      .filter((f) => f.relatedEntityId)
      .map((f) => f.relatedEntityId)
  )

  for (const entity of (currentSystemEntitiesData.entities || [])) {
    if (entity.id === parentEntity.id || children.some((c) => c.id === entity.id)) {
      continue
    }

    // This entity is in currentSystemEntitiesData but is NOT directly referenced by root
    // Check if parent entity's fields reference it
    if (!rootFieldEntityIds.has(entity.id)) {
      const matchingParentField = parentFields.find((f) => f.relatedEntityId === entity.id)
      if (matchingParentField) {
        children.push({
          ...entity,
          isOneToMany: matchingParentField.cardinality === 'one-to-many'
        })
      }
    }
  }

  return children
}

const blurEntityField = async (e, entity, indexEntityField) => {
  if (!system.events?.length) {
    return
  }

  const currentSchemas = workflow.value.schemas || []
  const schemaIndex = currentSchemas.findIndex((item) => item.name === entity.name)

  if (schemaIndex === -1 || Number(indexEntityField) === -1) {
    return
  }

  const schemaId = currentSchemas[schemaIndex].id
  const newName = (e.target.innerText || '').trim()
  const oldName = currentSchemas[schemaIndex].fields[indexEntityField].name

  if (newName === oldName || !validateFieldName(e, newName, oldName)) {
    return
  }

  const applyEntityFieldChange = (targetSchemas) => {
    const idx = targetSchemas.findIndex((s) => s.id === schemaId)
    if (idx !== -1) {
      targetSchemas[idx].fields[indexEntityField].name = newName
    }
  }

  if (shouldDeferSchemaUpdate(e)) {
    setPendingSchemaChange(applyEntityFieldChange)
    return
  }

  const newSchemas = safeStructuredClone(currentSchemas)
  applyPendingChanges(newSchemas)
  applyEntityFieldChange(newSchemas)

  await workflowApi.updateSchemas({
    schemas: newSchemas,
    workflow: workflow.value
  })
}

const recalculateBounds = () => {
  if (!systemDiv.value) {
    return
  }
  const elHeight = systemDiv.value.offsetHeight
  const vpHeight = window.innerHeight

  isTallerThanViewport.value = elHeight > vpHeight
  cachedMaxTop = STICKY_MARGIN
  cachedMinTop = -(elHeight - vpHeight + STICKY_MARGIN)

  if (isTallerThanViewport.value) {
    currentDynamicTop = Math.max(cachedMinTop, Math.min(cachedMaxTop, currentDynamicTop))
    stickyTop.value = Math.round(currentDynamicTop)
  } else {
    stickyTop.value = Math.max(STICKY_MARGIN, Math.round((vpHeight - elHeight) / 2))
  }
}

const onScroll = () => {
  const el = systemDiv.value
  if (!isTallerThanViewport.value || !el) {
    return
  }

  const scrollY = window.scrollY
  const delta = scrollY - lastScrollY
  lastScrollY = scrollY

  if (delta === 0) {
    return
  }

  currentDynamicTop = Math.max(cachedMinTop, Math.min(cachedMaxTop, currentDynamicTop - delta))
  el.style.top = Math.round(currentDynamicTop) + 'px'
}

const checkShouldFloat = () => {
  if (!outerDiv.value) {
    return
  }
  const newShouldFloat = !isSingleEventSelected.value && outerDiv.value.offsetHeight > window.innerHeight

  if (shouldFloat.value && !newShouldFloat) {
    isTallerThanViewport.value = false
    currentDynamicTop = STICKY_MARGIN
    lastScrollY = window.scrollY
    if (systemDiv.value) {
      systemDiv.value.style.top = ''
    }
  }

  shouldFloat.value = newShouldFloat

  if (shouldFloat.value) {
    recalculateBounds()
  }
}

const handleResize = () => {
  checkShouldFloat()
  requestAnimationFrame(() => {
    emit('refreshArrows')
  })
}

onMounted(() => {
  lastScrollY = window.scrollY
  checkShouldFloat()
  document.addEventListener('scroll', onScroll, { capture: true, passive: true })
  window.addEventListener('resize', handleResize, { passive: true })
  if (outerDiv.value) {
    resizeObserver.value = new ResizeObserver(() => {
      requestAnimationFrame(handleResize)
    })
    resizeObserver.value.observe(outerDiv.value)
  }
})

onUnmounted(() => {
  document.removeEventListener('scroll', onScroll, { capture: true })
  window.removeEventListener('resize', handleResize)
  if (resizeObserver.value) {
    resizeObserver.value.disconnect()
  }
})

watch(isSingleEventSelected, () => {
  nextTick(() => {
    checkShouldFloat()
  })
})
</script>

<style scoped>
.service-border {
  position: relative;
  border-radius: 10px;
  background-color: rgb(var(--v-theme-surface));
  border: 1px solid var(--service-border-color, var(--light-4));
}

.card-item-body {
  border-width: 1px;
  border-top-width: 8px;
  border-style: solid;
  width: 100%;
  font-size: 13px !important;
  border-bottom-left-radius: 10px !important;
  border-bottom-right-radius: 10px !important;
}

.field-icon-container {
  width: 16px;
  margin-right: 2px;
}

.required-field {
  &::after {
    content: ' *';
  }

  &:focus::after {
    visibility: hidden;
  }
}

.unused-field {
  color: var(--dark-1);
  opacity: 0.85;
}

.float-container {
  padding-top: 150px;
  padding-bottom: 150px;
}

.sticky-float {
  position: sticky !important;
}

.data-type-label {
  font-size: 10px;
  color: var(--dark-1);
  font-weight: 500;
  margin-left: 2px;
  user-select: none;
  flex-shrink: 0;
}

.root-fields-two-column {
  column-count: 2;
  column-gap: 16px;
}

.root-fields-two-column > .field-name {
  break-inside: avoid;
  -webkit-column-break-inside: avoid;
  page-break-inside: avoid;
}

</style>
